import { zodResolver } from "@hookform/resolvers/zod";
import { alterarSenhaSchema, type AlterarSenhaDto, type SessaoResposta } from "@zaphub/types";
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@zaphub/ui";
import { AlertTriangle, KeyRound } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { useSessao } from "../../lib/sessao";
import { aplicarErroApi, postarAuth } from "../../paginas/auth/api";
import { CampoSenha } from "./Campo";
import { RequisitosSenha } from "./RequisitosSenha";

function FormularioTroca({ aoSair }: { aoSair: () => void }) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [saindo, setSaindo] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AlterarSenhaDto>({
    resolver: zodResolver(alterarSenhaSchema),
    mode: "onTouched",
    defaultValues: { senhaAtual: "", novaSenha: "" },
  });
  const novaSenha = watch("novaSenha") ?? "";

  const trocar = handleSubmit(async (valores) => {
    setErroGeral(null);
    try {
      const sessao = await postarAuth<SessaoResposta>("/auth/alterar-senha", valores);
      // A API derruba as outras sessões e devolve uma nova para este dispositivo.
      const { definirToken, definirSessao, usuario, conta, impersonadoPor } = useSessao.getState();
      definirToken(sessao.accessToken);
      if (usuario) definirSessao({ usuario: { ...usuario, deveTrocarSenha: false }, conta, impersonadoPor });
      toast.success("Senha alterada.");
    } catch (erro) {
      setErroGeral(aplicarErroApi(erro, setError, ["senhaAtual", "novaSenha"]));
    }
  });

  const sair = async () => {
    setSaindo(true);
    try {
      await api.post("/auth/logout");
    } catch {
      /* sai mesmo assim: a sessão local é descartada */
    } finally {
      useSessao.getState().limpar();
      aoSair();
      setSaindo(false);
    }
  };

  return (
    <form onSubmit={trocar} noValidate className="grid gap-4" aria-label="Trocar senha">
      {erroGeral && (
        <div
          role="alert"
          className="flex gap-2.5 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p>{erroGeral}</p>
        </div>
      )}
      <CampoSenha
        rotulo="Senha atual (a provisória)"
        autoComplete="current-password"
        autoFocus
        erro={errors.senhaAtual?.message}
        {...register("senhaAtual")}
      />
      <CampoSenha
        rotulo="Nova senha"
        autoComplete="new-password"
        erro={errors.novaSenha?.message}
        dica={<RequisitosSenha senha={novaSenha} />}
        {...register("novaSenha")}
      />
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <Button type="button" variante="fantasma" onClick={sair} disabled={saindo || isSubmitting}>
          Sair
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Salvando…" : "Trocar senha e continuar"}
        </Button>
      </div>
    </form>
  );
}

/**
 * Bloqueia TODO o painel enquanto `deveTrocarSenha` for verdadeiro (senha provisória).
 * Montado uma vez na raiz do app, então vale para qualquer tela e após recarregar a página.
 * Não fecha por Esc nem por clique fora; a única saída além de trocar é encerrar a sessão.
 */
export function BloqueioTrocaSenha({ aoSair }: { aoSair: () => void }) {
  const obrigatoria = useSessao((s) => Boolean(s.usuario?.deveTrocarSenha) && !s.impersonadoPor);
  return (
    <Dialog open={obrigatoria}>
      <DialogContent
        className="[&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mb-1 grid size-10 place-items-center rounded-md border bg-muted text-primary">
            <KeyRound className="size-5" aria-hidden="true" />
          </div>
          <DialogTitle>Troque sua senha para continuar</DialogTitle>
          <DialogDescription>
            Sua senha atual é provisória. Defina uma senha só sua; as outras sessões abertas serão encerradas.
          </DialogDescription>
        </DialogHeader>
        <FormularioTroca aoSair={aoSair} />
      </DialogContent>
    </Dialog>
  );
}
